import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { TID } from '../constants/testIds';

export default function Login() {
    const { user, loading, refresh } = useAuth();
    const [mode, setMode] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (loading) return <div className="p-16 text-neutral-500">Loading…</div>;
    if (user) return <Navigate to="/dashboard" replace />;

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/dashboard';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const submitEmail = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
            const payload = mode === 'login' ? { email, password } : { email, password, name };
            await api.post(endpoint, payload);
            await refresh();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Something went wrong. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[70vh] grid grid-cols-1 lg:grid-cols-2">
            <div className="hidden lg:block relative overflow-hidden border-r border-neutral-200">
                <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/OakvilleOnLighthouse.jpg/1280px-OakvilleOnLighthouse.jpg"
                    alt="Bronte Harbour lighthouse, Oakville"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-brand-navy/40 flex items-end p-12">
                    <div className="text-white">
                        <div className="overline text-white/70">Bronte Harbour · Oakville</div>
                        <div className="font-editorial text-[36px] leading-tight tracking-editorial mt-4 max-w-md">
                            Save neighbourhoods, revisit comparisons, and get the weekly briefing.
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center px-8 py-16">
                <div className="w-full max-w-md">
                    <div className="overline">Sign in</div>
                    <h1 className="font-editorial text-[40px] tracking-editorial mt-4 leading-tight">
                        Welcome back.
                    </h1>
                    <p className="mt-4 text-[14px] text-neutral-600">
                        We only keep your email and name — enough to save your work and send the briefing, nothing more.
                    </p>

                    <button
                        data-testid={TID.login.google}
                        onClick={handleGoogle}
                        className="mt-8 w-full flex items-center justify-center gap-3 border border-brand-navy bg-brand-navy text-white py-3.5 text-[14px] rounded-lg hover:bg-brand-gold hover:text-brand-navy hover:border-brand-gold transition-colors"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M21.6 12.227c0-.727-.065-1.427-.187-2.1H12v3.973h5.383a4.599 4.599 0 0 1-1.996 3.02v2.51h3.23c1.891-1.743 2.983-4.31 2.983-7.403z"/>
                            <path d="M12 22c2.7 0 4.963-.897 6.617-2.427l-3.23-2.51c-.897.603-2.043.96-3.387.96-2.607 0-4.813-1.76-5.6-4.127H3.06v2.59A9.996 9.996 0 0 0 12 22z"/>
                            <path d="M6.4 13.9a5.997 5.997 0 0 1 0-3.803V7.507H3.06a10.002 10.002 0 0 0 0 8.983L6.4 13.9z"/>
                            <path d="M12 5.977c1.47 0 2.783.507 3.82 1.5l2.867-2.867C16.957 3.007 14.693 2 12 2A9.996 9.996 0 0 0 3.06 7.507L6.4 10.097C7.187 7.73 9.393 5.977 12 5.977z"/>
                        </svg>
                        Continue with Google
                    </button>

                    <div className="my-7 flex items-center gap-4 text-[11px] uppercase tracking-[0.2em] text-neutral-400 font-mono-data">
                        <span className="flex-1 h-px bg-neutral-200" />
                        or with email
                        <span className="flex-1 h-px bg-neutral-200" />
                    </div>

                    <form onSubmit={submitEmail} className="space-y-4" data-testid="login-email-form">
                        {mode === 'register' && (
                            <div>
                                <label htmlFor="login-name" className="bn-label block mb-1.5">Name</label>
                                <input
                                    id="login-name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    placeholder="Jordan Tremblay"
                                    className="bn-input w-full"
                                    data-testid="login-name-input"
                                />
                            </div>
                        )}
                        <div>
                            <label htmlFor="login-email" className="bn-label block mb-1.5">Email</label>
                            <input
                                id="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="you@example.ca"
                                className="bn-input w-full"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="login-password" className="bn-label block mb-1.5">Password</label>
                            <input
                                id="login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={8}
                                placeholder={mode === 'login' ? 'Your password' : 'At least 8 characters'}
                                className="bn-input w-full"
                                data-testid="login-password-input"
                            />
                        </div>
                        {error && (
                            <div className="text-[13px] text-[#C3341C] border-l-2 border-[#C3341C] pl-3 py-1" role="alert" data-testid="login-error">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full border border-brand-navy text-brand-navy py-3.5 text-[14px] rounded-lg hover:bg-brand-navy hover:text-white transition-colors disabled:opacity-60"
                            data-testid="login-email-submit"
                        >
                            {busy ? 'One moment…' : (mode === 'login' ? 'Sign in' : 'Create account')}
                        </button>
                    </form>

                    <button
                        onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                        className="mt-5 text-[13px] text-neutral-600 underline underline-offset-4 hover:text-brand-navy"
                        data-testid="login-mode-toggle"
                    >
                        {mode === 'login' ? "New here? Create an account" : 'Already have an account? Sign in'}
                    </button>

                    <div className="mt-8 text-[12px] text-neutral-500">
                        By continuing, you agree to our <Link to="/terms" className="underline underline-offset-2 hover:text-brand-navy">terms</Link> and <Link to="/privacy" className="underline underline-offset-2 hover:text-brand-navy">privacy policy</Link>.
                    </div>
                    <div className="mt-2 text-[12px] text-neutral-400">
                        Just browsing? Everything except saved favourites works <Link to="/neighbourhoods" className="underline underline-offset-2 hover:text-brand-navy">without an account</Link>.
                    </div>
                </div>
            </div>
        </div>
    );
}
